import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';

const CelebracionGrammy = () => {
  const [gallery, setGallery] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedIndex, setSelectedIndex] = useState(null);

  useEffect(() => {
    fetch(`/data/celebracion-grammy.json?v=${Date.now()}`, { cache: 'no-store' })
      .then((r) => r.ok ? r.json() : Promise.resolve({ images: [] }))
      .then((data) => {
        setGallery(Array.isArray(data.images) ? data.images : []);
      })
      .catch(() => setGallery([]))
      .finally(() => setIsLoading(false));
  }, []);

  // Keyboard navigation for lightbox
  useEffect(() => {
    if (selectedIndex === null) return;

    const onKey = (e) => {
      if (e.key === 'Escape') {
        setSelectedIndex(null);
      } else if (e.key === 'ArrowLeft') {
        setSelectedIndex((i) => (i - 1 + gallery.length) % gallery.length);
      } else if (e.key === 'ArrowRight') {
        setSelectedIndex((i) => (i + 1) % gallery.length);
      }
    };

    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [selectedIndex, gallery.length]);

  return (
    <div className="min-h-screen">
      <div className="max-w-7xl mx-auto px-6 py-12">
        <div className="bg-white rounded-2xl shadow-xl p-8 md:p-12 border-t-4 border-[#0b742f] relative overflow-hidden">
          <h1 className="text-3xl md:text-4xl font-bold mb-6">DOS FIGURAS DEL CENID HACEN HISTORIA EN LOS GRAMMY®</h1>

          <p className="text-gray-700 leading-relaxed mb-4 text-center">
            La <strong>Dra. Claudia Correa</strong> y el <strong>Dr. Antonio Vergara</strong>, <strong>artistas</strong>, <strong>productores</strong>, <strong>ingenieros</strong>, <strong>académicos</strong> e <strong>investigadores</strong> del CENID, logran nominaciones al <strong>GRAMMY</strong> americano y latino, elevando el nombre de <strong>Iberoamérica</strong> desde el arte y la academia.
          </p>

          <p className="text-gray-700 leading-relaxed mb-4">
            El Centro de Investigación y Desarrollo (CENID) celebra con orgullo un acontecimiento histórico para su comunidad académica: dos de sus miembros, la Dra. Claudia Correa y el Dr. Antonio Vergara, han alcanzado hitos sin precedentes en la industria musical internacional al obtener nominaciones oficiales tanto en los GRAMMY® americanos como en los Latin GRAMMY®, posicionando el CENID en un escenario global donde convergen la educación, la investigación y la excelencia artística iberoamericana.
          </p>

          <p className="text-gray-700 leading-relaxed mb-4">
            Estas distinciones, otorgadas por la Recording Academy® (GRAMMY®) y la Latin Recording Academy® (Latin GRAMMY®), representan el más alto reconocimiento al mérito musical, técnico y creativo. Y es precisamente desde el CENID —institución mexicana dedicada a la formación, la investigación y la innovación educativa— que estas dos figuras emergen como referentes ineludibles del diálogo entre arte, cultura y academia.
          </p>

          <h2 className="text-2xl font-semibold mt-6 mb-3">UN VÍNCULO ESPECIAL CON MÉXICO: LA NOMINACIÓN AL LATIN GRAMMY® POR UN ÁLBUM DE MÚSICA TEJANA</h2>

          <p className="text-gray-700 leading-relaxed mb-4">
            Uno de los hechos más significativos para la comunidad mexicana del CENID es que la nominación al Latin GRAMMY® obtenida por Antonio y Claudia se debe al trabajo técnico que ambos realizaron —mezcla y masterización— para un álbum de música tejana, género profundamente arraigado en el norte de México y el sur de Estados Unidos.
          </p>

          <p className="text-gray-700 leading-relaxed mb-6">
            Que un álbum dentro del campo de la música regional mexicana haya sido llevado al terreno del Latin GRAMMY® gracias al trabajo profesional de dos investigadores del CENID constituye un motivo de celebración y orgullo institucional.
          </p>

          <h2 className="text-2xl font-semibold mt-6 mb-3">CLAUDIA CORREA: DE LA INVESTIGACIÓN A LA PRODUCCIÓN MUSICAL NOMINADA AL GRAMMY®</h2>

          <p className="text-gray-700 leading-relaxed mb-4">
            La Dra. Claudia Correa, docente investigadora del CENID y especialista en innovación educativa, cultura sonora y producción artística, ha marcado un precedente histórico al desempeñarse como productora, ingeniera de grabación y mezcla en el álbum nominado al GRAMMY® americano en la categoría Best Contemporary Blues Album, interpretado por el artista principal Dr. Antonio Vergara.
          </p>

          <p className="text-gray-700 leading-relaxed mb-4">
            Su participación técnica y creativa en este disco—grabado y producido en estudio profesional propio— introduce un triunfo simbólico para la comunidad mexicana e iberoamericana: una académica-investigadora con trayectoria en México, España, Argentina y Ecuador se abre paso en la ceremonia musical más prestigiosa del mundo.
          </p>

          <h2 className="text-2xl font-semibold mt-6 mb-3">ANTONIO VERGARA: PRIMER ECUATORIANO NOMINADO COMO ARTISTA PRINCIPAL EN SU CATEGORÍA EN LOS GRAMMY®</h2>

          <p className="text-gray-700 leading-relaxed mb-4">
            El Dr. Antonio Vergara, músico, guitarrista, productor musical y docente investigador del CENID, alcanzó un logro histórico al convertirse en el primer ecuatoriano nominado como artista principal en la categoría Best Contemporary Blues Album en los GRAMMY® gracias a su obra <em>The Fury</em>.
          </p>

          <p className="text-gray-700 leading-relaxed mb-6">
            Vergara, además, es miembro con derecho a voto tanto de la Recording Academy® como de la Latin Recording Academy®, consolidando su presencia como figura artística internacional y académico de alto nivel.
          </p>

          <h2 className="text-2xl font-semibold mt-6 mb-3">CENID: CUNA DE INVESTIGACIÓN, ARTE Y EXCELENCIA INTERDISCIPLINARIA</h2>

          <div className="text-gray-700 leading-relaxed">
            <p className="mb-3">La relación de la Dra. Correa y el Dr. Vergara con el CENID no es reciente ni superficial. Ambos desarrollaron aquí sus investigaciones posdoctorales en Política Educativa, Estudios Sociales y Culturales, consolidando una sólida trayectoria académica que se suma a sus aportes como:</p>
            <ul className="list-disc pl-6 mb-4 text-gray-700">
              <li>docentes de posgrado,</li>
              <li>investigadores activos,</li>
              <li>tutores y evaluadores,</li>
              <li>miembros académicos del Centro,</li>
              <li>y autores de artículos científicos con afiliación institucional al CENID.</li>
            </ul>

            <p className="mb-4">Hoy, sus logros en los GRAMMY® y Latin GRAMMY® no solo son triunfos personales: representan un avance para la educación, la cultura y el arte iberoamericano, fortaleciendo la proyección internacional del CENID como institución que integra y celebra la excelencia académica y creativa.</p>

            <p className="mb-6">El CENID celebra, honra y comparte con orgullo este acontecimiento que marca un antes y un después en su historia institucional. La Dra. Claudia Correa y el Dr. Antonio Vergara son prueba viva de que la academia y el arte no solo pueden coexistir, sino elevarse mutuamente para transformar comunidades, abrir fronteras y llevar el nombre de Latinoamérica —y de México— a los escenarios más prestigiosos del mundo.</p>
          </div>

          <div className="mt-8">
            <Link to="/" className="inline-flex items-center gap-2 bg-[#0b742f] text-white px-4 py-2 rounded-md">
              Volver al inicio
            </Link>
          </div>

          {/* Gallery Section - parametrizable desde JSON */}
          <div className="mt-10">
            <h3 className="text-2xl font-semibold mb-4">Galería de imágenes</h3>
            {isLoading ? (
              <div className="text-gray-600">Cargando galería…</div>
            ) : (
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                {gallery.length === 0 ? (
                  <div className="col-span-2 text-gray-600">No hay imágenes disponibles.</div>
                ) : (
                  gallery.map((img, idx) => (
                    <figure key={idx} className="bg-white rounded-lg overflow-hidden shadow-sm cursor-pointer relative" onClick={() => setSelectedIndex(idx)}>
                          <div className="w-full h-80 md:h-96 bg-gray-50 flex items-center justify-center overflow-hidden">
                            <img src={img.src} alt={img.caption || `imagen-${idx+1}`} className="h-full w-auto object-contain mx-auto" onError={(e) => { e.target.onerror = null; e.target.src = 'https://recursos.cenid.org/img_cenid/placeholder.jpg'; }} />
                          </div>
                      <div className="absolute inset-0 flex items-start justify-end p-2 pointer-events-none">
                        <span className="bg-white/90 rounded-full p-1 text-gray-700 text-sm shadow">🔍</span>
                      </div>
                      {img.caption && <figcaption className="p-2 text-sm text-gray-600">{img.caption}</figcaption>}
                    </figure>
                  ))
                )}
              </div>
            )}

            {/* Lightbox modal */}
            {selectedIndex !== null && gallery[selectedIndex] && (
              <div
                className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4"
                onClick={() => setSelectedIndex(null)}
                role="dialog"
                aria-modal="true"
              >
                <div className="max-w-4xl w-full relative" onClick={(e) => e.stopPropagation()}>
                    <div className="relative">
                      <button
                        className="absolute top-3 right-3 bg-white/90 text-gray-700 rounded-full p-2 shadow-md hover:bg-white z-20"
                        onClick={() => setSelectedIndex(null)}
                        aria-label="Cerrar galería"
                      >
                        ✕
                      </button>

                      <button
                        className="absolute left-3 top-1/2 -translate-y-1/2 bg-white/90 text-gray-700 rounded-full p-2 shadow-md hover:bg-white z-20"
                        onClick={() => setSelectedIndex((i) => (i - 1 + gallery.length) % gallery.length)}
                        aria-label="Anterior"
                      >
                        ‹
                      </button>

                      <button
                        className="absolute right-3 top-1/2 -translate-y-1/2 bg-white/90 text-gray-700 rounded-full p-2 shadow-md hover:bg-white z-20"
                        onClick={() => setSelectedIndex((i) => (i + 1) % gallery.length)}
                        aria-label="Siguiente"
                      >
                        ›
                      </button>
                    </div>

                    <div className="bg-white rounded-lg overflow-hidden p-6 shadow-lg">
                      <img
                        src={gallery[selectedIndex].src_full || gallery[selectedIndex].src}
                        alt={gallery[selectedIndex].caption || `imagen-${selectedIndex+1}`}
                        className="w-full object-contain mx-auto"
                        style={{ maxHeight: 'calc(75vh - 48px)' }}
                        onError={(e) => { e.target.onerror = null; e.target.src = 'https://recursos.cenid.org/img_cenid/placeholder.jpg'; }}
                      />
                      {gallery[selectedIndex].caption && (
                        <div className="pt-3 text-sm text-gray-700 text-center">{gallery[selectedIndex].caption}</div>
                      )}
                    </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CelebracionGrammy;
